import { EnvironmentProviders, inject, Injectable, provideEnvironmentInitializer } from '@angular/core';

/**
 * Form fields that take text input. Hidden fields, checkboxes, radio buttons, files, buttons, ranges and
 * colours cannot be autofilled with personal data and are left alone.
 */
const TEXT_FIELDS =
    'input:not([type=hidden]):not([type=checkbox]):not([type=radio]):not([type=file]):not([type=button])' +
    ':not([type=submit]):not([type=reset]):not([type=range]):not([type=color]):not([type=image]), textarea';
const TARGETS = `${TEXT_FIELDS}, form`;

/**
 * Switches the browser's autocomplete off for every form and text field of the app
 * (BSI TR-03161-2 O.Data_9, O.Data_18).
 *
 * The app handles personal and health data in almost every form (contacts, health values, medication,
 * addresses, emergency information, ...). Instead of relying on every template to remember
 * `autocomplete="off"`, this service sets it on each form and field as soon as it is added to the page,
 * including fields inside dialogs, overlays and lazy-loaded routes.
 *
 * A field or form that sets `autocomplete` itself (for example `new-password`) keeps its value.
 */
@Injectable({ providedIn: 'root' })
export class AutocompleteOffService {
    private _observer: MutationObserver | null = null;

    start(): void {
        if (this._observer || typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
            return;
        }

        this._apply(document.body);

        this._observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                mutation.addedNodes.forEach((node) => {
                    if (node instanceof HTMLElement) {
                        this._apply(node);
                    }
                });
            }
        });
        this._observer.observe(document.body, { childList: true, subtree: true });
    }

    stop(): void {
        this._observer?.disconnect();
        this._observer = null;
    }

    private _apply(root: HTMLElement): void {
        if (root.matches(TARGETS)) {
            this._off(root);
        }
        // Skip the subtree walk for elements that cannot contain a form field (icons, charts, text nodes' parents)
        if (root.childElementCount) {
            root.querySelectorAll(TARGETS).forEach((element) => this._off(element));
        }
    }

    private _off(element: Element): void {
        if (!element.hasAttribute('autocomplete')) {
            element.setAttribute('autocomplete', 'off');
        }
    }
}

/**
 * Starts the {@link AutocompleteOffService} when the app starts.
 */
export function provideAutocompleteOff(): EnvironmentProviders {
    return provideEnvironmentInitializer(() => inject(AutocompleteOffService).start());
}
